<template>
  <div class="trainings-hub">
    <!-- Decorative background blobs -->
    <div class="bg-blob bg-blob-1"></div>
    <div class="bg-blob bg-blob-2"></div>
    <div class="bg-blob bg-blob-3"></div>

    <!-- HERO SECTION -->
    <section class="hero trainings-hero">
      <div class="hero-body">
        <div class="container">
          <div class="columns is-vcentered">
            <!-- Left: copy + CTAs -->
            <div class="column is-7">
              <div class="eyebrow">
                <span class="dot"></span>
                Wingubox Learning Hub
              </div>
              <h1 class="title is-1 hero-title">
                Learn Wingubox
                <span class="hero-highlight">like a pro.</span>
              </h1>
              <p class="subtitle is-5 hero-subtitle">
                Live walkthroughs, bite-sized feature demos, and deep dives into payroll,
                leave, and time & attendance (and more). All in one place.
              </p>

              <div class="hero-cta-row">
                <button class="button is-dark is-medium hero-cta" @click="showModal = true">
                  <span class="material-icons mr-2">notifications_active</span>
                  Get training alerts
                </button>

                <button
                  class="button is-white is-medium hero-cta secondary"
                  @click="setTimeFilter('upcoming')"
                >
                  <span class="material-icons mr-2">play_circle_outline</span>
                  Jump to upcoming sessions
                </button>
              </div>

              <div class="hero-tags">
                <span class="tag is-light is-rounded">Payroll Deep Dives</span>
                <span class="tag is-light is-rounded">Leave Module</span>
                <span class="tag is-light is-rounded">Time & Attendance</span>
                <span class="tag is-light is-rounded">HRMplus</span>
                <span class="tag is-light is-rounded">Commerce</span>
              </div>
            </div>

            <!-- Right: stats + next session -->
            <div class="column is-5">
              <div class="hero-right-stack">
                <!-- Stats card -->
                <div class="stats-card glass-card">
                  <div class="stats-row">
                    <div class="stat">
                      <p class="stat-label">Total sessions</p>
                      <p class="stat-value">{{ totalTrainings }}</p>
                    </div>
                    <div class="stat">
                      <p class="stat-label">Upcoming</p>
                      <p class="stat-value">{{ upcomingCount }}</p>
                    </div>
                    <div class="stat">
                      <p class="stat-label">Recordings</p>
                      <p class="stat-value">{{ pastCount }}</p>
                    </div>
                  </div>
                  <p class="stat-footer">
                    New trainings are added regularly. Stay tuned!
                  </p>
                </div>

                <!-- Next upcoming -->
                <div v-if="nextUpcomingTraining" class="next-card glass-card">
                  <p class="next-label">
                    <span class="material-icons tiny-icon mr-1">bolt</span>
                    Next live Wingubox session
                  </p>
                  <p class="next-title">
                    {{ nextUpcomingTraining.title }}
                  </p>

                  <div class="next-meta">
                    <p>
                      <span class="material-icons tiny-icon mr-1">event</span>
                      {{ formatDate(nextUpcomingTraining.date) }}
                    </p>
                    <p>
                      <span class="material-icons tiny-icon mr-1">schedule</span>
                      {{ formatTime(nextUpcomingTraining.time) }}
                    </p>
                    <p>
                      <span class="material-icons tiny-icon mr-1">location_on</span>
                      {{ nextUpcomingTraining.location }}
                    </p>
                  </div>

                  <a
                    :href="nextUpcomingTraining.training_link"
                    target="_blank"
                    class="button is-primary is-fullwidth is-small mt-3"
                  >
                    Reserve my seat
                  </a>
                </div>

                <!-- No upcoming -->
                <div v-else class="next-card glass-card empty-next">
                  <p class="next-label">
                    <span class="material-icons tiny-icon mr-1">event_busy</span>
                    No live session scheduled (yet)
                  </p>
                  <p class="is-size-7">
                    Subscribe and we’ll ping you the moment we publish the next live training.
                  </p>
                  <button
                    class="button is-white is-small mt-3"
                    @click="showModal = true"
                  >
                    Notify me when it’s live
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Filter + search bar -->
          <div class="filters-layout-bar">
            <div class="filters-left">
              <!-- Time filter chips -->
              <div class="filters-chip-group">
                <button
                  class="chip"
                  :class="{ active: activeFilter === 'past' }"
                  @click="setTimeFilter('past')"
                >
                  <span class="material-icons tiny-icon mr-1">history</span>
                  Past / Recordings
                </button>

                <button
                  class="chip"
                  :class="{ active: activeFilter === 'upcoming' }"
                  @click="setTimeFilter('upcoming')"
                >
                  <span class="material-icons tiny-icon mr-1">schedule</span>
                  Upcoming
                </button>

                <button
                  class="chip"
                  :class="{ active: activeFilter === 'all' }"
                  @click="setTimeFilter('all')"
                >
                  <span class="material-icons tiny-icon mr-1">all_inclusive</span>
                  All
                </button>
              </div>

              <!-- App filter chips -->
              <div class="app-filter" v-if="appsList.length">
                <span class="filter-label">App</span>
                <button
                  class="chip small"
                  :class="{ active: activeAppFilter === 'all' }"
                  @click="setAppFilter('all')"
                >
                  All apps
                </button>
                <button
                  v-for="app in appsList"
                  :key="app"
                  class="chip small"
                  :class="{ active: activeAppFilter === app }"
                  @click="setAppFilter(app)"
                >
                  {{ app }}
                </button>
              </div>
            </div>

            <!-- Search -->
            <div class="filters-right">
              <div class="field has-addons search-wrapper">
                <div class="control has-icons-left is-expanded">
                  <input
                    v-model="searchQuery"
                    class="input is-small search-input"
                    type="text"
                    placeholder="Search trainings (title, app, description, location)"
                  />
                  <span class="icon is-left is-small">
                    <span class="material-icons tiny-icon">search</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- MAIN CONTENT -->
    <section class="section trainings-main">
      <div class="container">
        <!-- LOADING STATE -->
        <div v-if="loadingData" class="loading-grid">
          <div v-for="n in 6" :key="n" class="skeleton-card">
            <div class="skeleton-thumb"></div>
            <div class="skeleton-line long"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line short"></div>
          </div>
        </div>

        <!-- CONTENT -->
        <div v-else>
          <!-- Empty state -->
          <div v-if="!hasTrainings" class="empty-state">
            <div class="empty-pill">
              <span class="material-icons">event_busy</span>
            </div>
            <h2 class="title is-4 mb-5">
              No trainings match your filters
            </h2>
            <p class="subtitle is-6 mb-4">
              Try clearing the search, changing the time filter, or selecting a different app.
              You can also subscribe and we’ll email you when new trainings are added.
            </p>
            <button class="button is-dark" @click="showModal = true">
              Get email notifications
            </button>
          </div>

          <!-- Grid layout -->
          <div v-else class="columns is-multiline grid-wrapper">
            <div
              class="column is-one-third-desktop is-half-tablet is-full-mobile"
              v-for="training in filteredTrainings"
              :key="training.id"
            >
              <div
                class="card grid-card hover-lift"
                :class="{
                  'grid-card-past': isPastTraining(training.date),
                  'grid-card-upcoming': !isPastTraining(training.date),
                  expanded: selectedTraining.id === training.id
                }"
              >
                <div class="card-image">
                  <figure
                    v-if="!isPastTraining(training.date) && training.images && training.images.length"
                    class="image is-16by9"
                  >
                    <img :src="training.images[0].image" alt="Training Image" />
                  </figure>
                  <div
                    v-else-if="isPastTraining(training.date) && training.embedded_code"
                    class="video-shell"
                    v-html="training.embedded_code"
                  ></div>

                  <div class="corner-label">
                    <span class="material-icons tiny-icon mr-1">
                      {{ isPastTraining(training.date) ? 'play_circle_filled' : 'live_tv' }}
                    </span>
                    {{ isPastTraining(training.date) ? 'Recording' : 'Live / Upcoming' }}
                  </div>
                </div>

                <div class="card-content">
                  <div class="top-row">
                    <div class="badge-date">
                      <span class="badge-day">
                        {{ new Date(training.date).getDate() }}
                      </span>
                      <span class="badge-month">
                        {{
                          new Date(training.date).toLocaleString(undefined, {
                            month: 'short'
                          })
                        }}
                      </span>
                    </div>
                    <div class="top-text">
                      <h3 class="title is-6 mb-1">{{ training.title }}</h3>
                      <p class="is-size-7 has-text-grey">
                        {{ formatDate(training.date) }} · {{ formatTime(training.time) }}
                      </p>
                      <p v-if="training.app_category" class="is-size-7 app-pill">
                        <span class="material-icons tiny-icon mr-1">apps</span>
                        {{ training.app_category }}
                      </p>
                    </div>
                  </div>

                  <p class="is-size-7 has-text-grey-dark mt-2 mb-3">
                    <span class="material-icons tiny-icon mr-1">location_on</span>
                    {{ training.location }}
                  </p>

                  <div class="grid-actions">
                    <button
                      class="button is-small is-light"
                      @click="toggleDetails(training)"
                    >
                      {{
                        selectedTraining.id === training.id
                          ? 'Hide details'
                          : 'More details'
                      }}
                    </button>
                    <div class="buttons is-right">
                      <a
                        v-if="!isPastTraining(training.date)"
                        target="_blank"
                        :href="training.training_link"
                        class="button is-primary is-small"
                      >
                        Register
                      </a>
                      <button
                        v-else
                        @click="viewRecording(training)"
                        class="button is-primary is-small"
                      >
                        Watch
                      </button>
                    </div>
                  </div>

                  <transition name="fade-slide">
                    <div
                      v-if="selectedTraining.id === training.id"
                      class="expanded-content"
                    >
                      <h4 class="title is-6 mb-1">What we’ll cover</h4>
                      <p class="is-size-7 mb-3" v-html="training.extended_description"></p>
                      <hr />
                      <h4 class="title is-6 mb-1">Key takeaways</h4>
                      <p class="is-size-7" v-html="training.learning_outcomes"></p>
                    </div>
                  </transition>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SUBSCRIBE MODAL -->
    <div class="modal" :class="{ 'is-active': showModal }">
      <div class="modal-background" @click="showModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Stay in the loop</p>
          <button @click="showModal = false" class="delete ml-2" aria-label="close"></button>
        </header>
        <section class="modal-card-body">
          <p class="is-size-7 mb-4">
            Pop in your email and we’ll only send you updates when new Wingubox
            trainings go live or new recordings drop. No newsletters, no spam.
          </p>
          <form class="form" @submit.prevent="subscribe">
            <div v-if="isIncorrectCredentials" class="notification is-danger is-light">
              <button @click="isIncorrectCredentials = false" class="delete"></button>
              Email already registered!
            </div>
            <div class="field">
              <label class="label">Email</label>
              <div class="control">
                <input
                  v-model="email"
                  required
                  class="input"
                  type="email"
                  placeholder="you@company.com"
                />
              </div>
            </div>
            <div class="field">
              <div class="control">
                <label class="checkbox is-size-7">
                  <input v-model="isSubscribed" type="checkbox" required />
                  I’m happy to receive Wingubox training notifications.
                </label>
              </div>
            </div>
          </form>
        </section>
        <footer class="modal-card-foot">
          <button
            v-if="!isAuthenticating"
            class="button is-success"
            @click.prevent="subscribe"
          >
            Subscribe
          </button>
          <button v-else class="button is-success" disabled>
            Subscribing...
          </button>
          <button class="button" @click="showModal = false">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toast-notification';

export default {
  data() {
    return {
      trainings: [],
      selectedTraining: {},
      showModal: false,
      email: '',
      isSubscribed: false,
      isIncorrectCredentials: false,
      isAuthenticating: false,
      loadingData: true,
      activeFilter: 'past', 
      activeAppFilter: 'all',
      searchQuery: '',
    };
  },
  computed: {
    totalTrainings() {
      return this.trainings.length;
    },
    upcomingCount() {
      const now = new Date();
      return this.trainings.filter((t) => new Date(t.date) >= now).length;
    },
    pastCount() {
      const now = new Date();
      return this.trainings.filter((t) => new Date(t.date) < now).length;
    },
    nextUpcomingTraining() {
      const now = new Date();
      const upcoming = this.trainings
        .filter((t) => new Date(t.date) >= now)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      return upcoming.length ? upcoming[0] : null;
    },
    appsList() {
      // field to be added in the API
      const set = new Set();
      this.trainings.forEach((t) => {
        if (t.app_category) {
          set.add(t.app_category);
        }
      });
      return Array.from(set).sort();
    },
    filteredTrainings() {
      const now = new Date();
      let list = [...this.trainings];

      // Time filter
      if (this.activeFilter === 'upcoming') {
        list = list.filter((t) => new Date(t.date) >= now);
      } else if (this.activeFilter === 'past') {
        list = list.filter((t) => new Date(t.date) < now);
      }

      // App filter
      if (this.activeAppFilter !== 'all') {
        list = list.filter(
          (t) =>
            (t.app_category || '').toLowerCase() ===
            this.activeAppFilter.toLowerCase()
        );
      }

      // Search filter
      const q = this.searchQuery.trim().toLowerCase();
      if (q) {
        list = list.filter((t) => {
          const haystack = [
            t.title || '',
            t.location || '',
            t.app_category || '',
            t.extended_description || '',
            t.learning_outcomes || '',
          ]
            .join(' ')
            .toLowerCase();
          return haystack.includes(q);
        });
      }

      // Sort by date ascending (upcoming first)
      list.sort((a, b) => new Date(a.date) - new Date(b.date));
      return list;
    },
    hasTrainings() {
      return this.filteredTrainings && this.filteredTrainings.length > 0;
    },
  },
  methods: {
    async fetchTrainings() {
      try {
        const response = await axios.get('https://community.wingubox.com/api/v1/trainings/');
        this.trainings = response.data || [];
      } catch (error) {
        console.error('Error fetching trainings:', error);
      } finally {
        this.loadingData = false;
      }
    },
    formatDate(date) {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(date).toLocaleDateString(undefined, options);
    },
    formatTime(time) {
      if (!time) return '';
      const options = { hour: '2-digit', minute: '2-digit' };
      return new Date(`1970-01-01T${time}Z`).toLocaleTimeString(undefined, options);
    },
    isPastTraining(date) {
      return new Date(date) < new Date();
    },
    toggleDetails(training) {
      if (this.selectedTraining.id === training.id) {
        this.selectedTraining = {};
      } else {
        this.selectedTraining = training;
      }
    },
    setTimeFilter(filter) {
      this.activeFilter = filter;
      this.selectedTraining = {};
    },
    setAppFilter(app) {
      this.activeAppFilter = app;
      this.selectedTraining = {};
    },
    async subscribe() {
      const toast = useToast();
      this.isAuthenticating = true;

      try {
        await axios.post('https://community.wingubox.com/api/v1/registered-emails/', {
          email: this.email,
          is_subscribed: this.isSubscribed,
        });

        this.isAuthenticating = false;
        this.showModal = false;
        this.email = '';
        this.isSubscribed = false;
        this.isIncorrectCredentials = false;
        toast.success('Subscription successful!');
      } catch (error) {
        this.isAuthenticating = false;
        this.isIncorrectCredentials = true;

        const msg =
          (error && error.response && error.response.data && error.response.data.email) ||
          (error && error.response && error.response.data && error.response.data.detail) ||
          'Unable to subscribe.';
        toast.error('Error subscribing: ' + msg);
      }
    },
    viewRecording(training) {
      const toast = useToast();
      if (training.youtube_link) {
        window.open(training.youtube_link, '_blank');
      } else {
        toast.error('Recording unavailable!');
      }
    },
  },
  created() {
    this.fetchTrainings();
  },
};
</script>

<style scoped>
@import 'bulma/css/bulma.min.css';
@import '@fortawesome/fontawesome-free/css/fontawesome.min.css';
@import 'material-icons/iconfont/material-icons.css';

.trainings-hub {
  position: relative;
  min-height: 100vh;
  background: radial-gradient(circle at top left, #e0f4ff 0, #f8fafc 45%, #f1f5f9 100%);
  overflow: hidden;
}

/* background blobs */
.bg-blob {
  position: absolute;
  border-radius: 999px;
  opacity: 0.75;
  filter: blur(40px);
  pointer-events: none;
}

.bg-blob-1 {
  width: 320px;
  height: 320px;
  background: #0cb0f1;
  top: -80px;
  left: -60px;
  animation: float 18s infinite alternate;
}

.bg-blob-2 {
  width: 260px;
  height: 260px;
  background: #4f46e5;
  top: 120px;
  right: -80px;
  animation: float 22s infinite alternate-reverse;
}

.bg-blob-3 {
  width: 220px;
  height: 220px;
  background: #22c55e;
  bottom: -60px;
  left: 30%;
  animation: float 26s infinite alternate;
}

@keyframes float {
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(20px, -20px, 0);
  }
}

/* HERO */
.trainings-hero {
  background: transparent;
  padding-top: 3.5rem;
  padding-bottom: 1rem;
}

.hero-body {
  position: relative;
  z-index: 2;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: #1d4ed8;
  margin-bottom: 0.75rem;
  background: rgba(255, 255, 255, 0.7);
  padding: 0.25rem 0.8rem;
  border-radius: 999px;
}

.eyebrow .dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #0cb0f1;
  margin-right: 0.4rem;
}

.hero-title {
  line-height: 1.1;
}

.hero-highlight {
  background: linear-gradient(120deg, #0cb0f1, #4f46e5);
  -webkit-background-clip: text;
  color: transparent;
}

.hero-subtitle {
  max-width: 32rem;
}

.hero-cta-row {
  margin-top: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.hero-cta.secondary {
  border: 1px solid rgba(148, 163, 184, 0.5);
}

.hero-tags {
  margin-top: 1.25rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* right side cards */
.hero-right-stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.glass-card {
  backdrop-filter: blur(14px);
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.9),
    rgba(255, 255, 255, 0.78)
  );
  border-radius: 18px;
  padding: 1rem 1.25rem;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.25);
}

.stats-card {
  font-size: 0.75rem;
}

.stats-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}

.stat-label {
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #64748b;
  font-size: 0.6rem;
}

.stat-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
}

.stat-footer {
  margin-top: 0.75rem;
  color: #6b7280;
}

.next-card .next-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: #6366f1;
  display: flex;
  align-items: center;
  margin-bottom: 0.4rem;
}

.next-card .next-title {
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.4rem;
}

.next-meta p {
  font-size: 0.75rem;
  color: #4b5563;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.empty-next .next-label {
  color: #f97316;
}

/* filter + search bar */
.filters-layout-bar {
  margin-top: 2.5rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
}

.filters-left {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.filters-chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.chip {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: rgba(255, 255, 255, 0.9);
  padding: 0.25rem 0.9rem;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.chip.small {
  padding: 0.15rem 0.7rem;
  font-size: 0.7rem;
}

.chip.active {
  background: linear-gradient(120deg, #0cb0f1, #4f46e5);
  color: white;
  border-color: transparent;
  box-shadow: 0 10px 25px rgba(37, 99, 235, 0.4);
}

.chip:hover {
  transform: translateY(-1px);
}

.app-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
}

.filter-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: #64748b;
  margin-right: 0.2rem;
}

.filters-right {
  min-width: 240px;
  max-width: 320px;
  width: 100%;
}

.search-wrapper .input.search-input {
  border-radius: 999px;
  font-size: 0.8rem;
  padding-left: 2rem;
}

/* MAIN SECTION */
.trainings-main {
  position: relative;
  z-index: 2;
}

/* loading skeleton */
.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.25rem;
}

.skeleton-card {
  border-radius: 16px;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.08);
}

.skeleton-thumb,
.skeleton-line {
  border-radius: 999px;
  background: linear-gradient(90deg, #e5e7eb 0, #f3f4f6 50%, #e5e7eb 100%);
  background-size: 200% 100%;
  animation: shimmer 1.3s infinite;
}

.skeleton-thumb {
  height: 140px;
  border-radius: 12px;
  margin-bottom: 0.75rem;
}

.skeleton-line {
  height: 7px;
  margin-bottom: 0.4rem;
}

.skeleton-line.long {
  width: 80%;
}

.skeleton-line.short {
  width: 40%;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* empty state */
.empty-state {
  text-align: center;
  padding: 4rem 1rem;
}

.empty-pill {
  width: 64px;
  height: 64px;
  border-radius: 999px;
  margin: 0 auto 1rem;
  background: radial-gradient(circle, #e5edff 0, #eff6ff 55%, #e5e7eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366f1;
}

/* GRID layout */
.grid-wrapper {
  margin-top: 1rem;
}

.grid-card {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.4);
}

.grid-card-upcoming {
  background: radial-gradient(circle at top left, #e0f2fe, #eff6ff);
}

.grid-card-past {
  background: radial-gradient(circle at top left, #f9fafb, #f3f4f6);
}

.video-shell {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  background: #000;
}

.video-shell > * {
  position: absolute;
  inset: 0;
}

.video-shell :deep(iframe) {
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  border: 0 !important;
  display: block;
}

.corner-label {
  position: absolute;
  z-index: 5;
  top: 0.75rem;
  left: 0.75rem;
  font-size: 0.7rem;
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.7rem;
  border-radius: 999px;
  backdrop-filter: blur(10px);
  background: rgba(15, 23, 42, 0.8);
  color: #f9fafb;
}

.top-row {
  display: flex;
  gap: 0.75rem;
}

.badge-date {
  min-width: 48px;
  text-align: center;
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.92);
  color: white;
  padding: 0.25rem 0.2rem;
}

.badge-day {
  display: block;
  font-weight: 700;
  font-size: 0.95rem;
}

.badge-month {
  display: block;
  text-transform: uppercase;
  font-size: 0.6rem;
  opacity: 0.85;
}

.app-pill {
  margin-top: 0.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  color: #4b5563;
}

.grid-actions {
  margin-top: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* hover */
.hover-lift {
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.hover-lift:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
  border-color: rgba(59, 130, 246, 0.6);
}

/* expanded details */
.expanded-content {
  padding: 0.9rem 1.25rem 1.25rem;
  border-top: 1px dashed rgba(148, 163, 184, 0.6);
  background: rgba(255, 255, 255, 0.82);
  font-size: 0.8rem;
}

/* animation */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.18s ease-out;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* modal tweaks */
.modal-card-body {
  font-size: 0.85rem;
}

/* icons */
.tiny-icon {
  font-size: 1rem;
}

/* responsive */
@media (max-width: 1024px) {
  .filters-layout-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .filters-right {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .hero-cta-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>